// src/hr/kit/Drawer.js -- right-side slide-in panel over a scrim.
// Modal: focus moves into the panel, Tab stays inside, Esc / scrim / Close button call onClose,
// and focus returns to whatever had it before the drawer opened. Rendered into document.body
// inside its own .hrp-root so the HR tokens and resets apply.
// Keys are handled on `document`, not on the panel: a focused control that becomes disabled
// (Btn loading) or unmounts drops focus to <body>, and Esc / Tab must keep working from there.
import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FiX } from 'react-icons/fi';
import { cx, plainId } from './util';

const FOCUSABLE = [
  'a[href]', 'area[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', 'iframe', '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

function focusablesIn(root) {
  if (!root) return [];
  return Array.from(root.querySelectorAll(FOCUSABLE)).filter(
    (el) => el.getAttribute('tabindex') !== '-1' && el.getAttribute('aria-hidden') !== 'true'
  );
}

// Open drawers, oldest first. Only the top one handles keys and guards focus, so a dialog opened
// from inside a drawer keeps working.
const openStack = [];
const isTop = (token) => openStack[openStack.length - 1] === token;

function focusFirstIn(panel) {
  const target = focusablesIn(panel)[0] || panel;
  if (target && target.focus) target.focus();
}

// onSubmit: when given, the panel is a <form> so a footer <Btn type="submit"> submits it.
export function Drawer({
  open, onClose, title, subtitle, children, footer, width = 380, initialFocusRef, onSubmit, className
}) {
  const titleId = `hrp-drawer-${plainId(useId())}`;
  const panelRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const token = {};
    openStack.push(token);
    const previous = document.activeElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      const panel = panelRef.current;
      if (!panel || !isTop(token)) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        if (onCloseRef.current) onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const active = document.activeElement;
      // Focus outside the panel (fell to <body>, or anywhere else): bring it back in.
      if (!panel.contains(active)) {
        e.preventDefault();
        focusFirstIn(panel);
        return;
      }
      const items = focusablesIn(panel);
      if (items.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (active === first || active === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || active === panel)) {
        e.preventDefault();
        first.focus();
      }
    };

    // Anything outside the panel that takes focus while the drawer is open gives it back.
    const onFocusIn = (e) => {
      const panel = panelRef.current;
      if (!panel || !isTop(token) || panel.contains(e.target)) return;
      focusFirstIn(panel);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', onFocusIn);

    const target = (initialFocusRef && initialFocusRef.current) || panelRef.current;
    if (target && target.focus) target.focus();

    return () => {
      // Listeners go first so restoring focus outside the panel isn't pulled back in.
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn);
      const at = openStack.indexOf(token);
      if (at !== -1) openStack.splice(at, 1);
      document.body.style.overflow = overflow;
      if (previous && previous.focus && document.contains(previous)) previous.focus();
    };
    // initialFocusRef is read once, when the drawer opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const Panel = onSubmit ? 'form' : 'div';
  const panelProps = onSubmit
    ? { onSubmit: (e) => { e.preventDefault(); onSubmit(e); }, noValidate: true }
    : {};

  return createPortal(
    <div className="hrp-root hrp-layer">
      <div className="hrp-scrim" data-testid="hrp-scrim" aria-hidden="true" onClick={() => onCloseRef.current && onCloseRef.current()} />
      <Panel
        ref={panelRef}
        className={cx('hrp-drawer', className)}
        style={{ '--hrp-drawer-w': `${width}px` }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        {...panelProps}
      >
        <div className="hrp-drawer__head">
          <div className="hrp-drawer__heading">
            <h2 id={titleId} className="hrp-drawer__title">{title}</h2>
            {subtitle && <p className="hrp-drawer__sub">{subtitle}</p>}
          </div>
          <button type="button" className="hrp-iconbtn" aria-label="Close" onClick={() => onCloseRef.current && onCloseRef.current()}>
            <FiX aria-hidden="true" />
          </button>
        </div>
        <div className="hrp-drawer__body">{children}</div>
        {footer && <div className="hrp-drawer__foot">{footer}</div>}
      </Panel>
    </div>,
    document.body
  );
}

export default Drawer;
